<template>
    <div style="width: 100%;">
        <div class="d-flex col-12">
            <div class="form-control-label" style="white-space: pre-line;margin-left: 4px;"><span v-if="question.is_required" class="required-star mr-1">*</span>{{title}}</div>
        </div>
        <div class="col-12"><p class="text-muted px-1 question-description" v-html="formattedText"></p></div>
        <div v-if="rowAnswerRequired" class="col-12"><p class="text-danger px-1"><small>{{ $t('row_answer_required') }}</small></p></div>
        <div class="col-12" v-if="question.image_file_name">
            <img class="cursor-pointer" :src="this.context.storageEndpoint + this.question.image_file_name" @click="openPhotoViewer = true"/>
        </div>
        <div v-if="!hasValidAnswer && showInvalid" class="col-12 pl-3 my-2">
            <div class="alert alert-warning">{{$t('message.please_select_your_answer')}}</div>
        </div>
        <div class="container-fluid my-2">
            <table class="w-100 ding-table">
                <tr>
                    <th></th>
                    <th v-for="(column, id) in columns" :key="id">{{ column }}</th>
                </tr>
                <tr v-for="(row, rid) in rows" :key="rid">
                    <th class="text-left">{{ row }}</th>
                    <td v-for="(column, cid) in columns" :key="cid">
                        <input :type="isMultiple ? 'checkbox' : 'radio'" :name="row" :checked="isChecked(row, column)" @click="toggle(row, column)">
                    </td>
                </tr>
            </table>
        </div>
        <photo-viewer
            v-if="openPhotoViewer"
            ref="photoViewer"
            :photos="photo"
            :context="context"
            @close="openPhotoViewer = false"></photo-viewer>
    </div>
</template>

<script>
import Constants from '../../constants';
import PhotoViewer from '../common/PhotoViewer.vue';

export default {
    components: { PhotoViewer },
    props: ['question', 'providerId', 'context', 'form', 'answer'],
    data() {
        const rows = this.question.content.data.rows ?? [];
        const columns = this.question.content.data.columns ?? [];

        let matrixChoices = [];
        let matrixChoicesMap = {};
        if (this.answer) {
            let answerColumns = [];
            let answerRows = [];

            if (this.answer.parsedContent?.matrixChoices) {
                matrixChoices = this.answer.parsedContent.matrixChoices;
                answerColumns = matrixChoices.map(choice => choice.split('/')[1]);
            }
            if (this.answer.parsedContent?.matrixChoicesMap) {
                matrixChoicesMap = this.answer.parsedContent.matrixChoicesMap;
                answerRows = Object.keys(matrixChoicesMap);
            }

            answerRows.forEach(answerRow => {
                const exist = rows.find(row => row === answerRow);
                if (!exist) {
                    rows.push(answerRow);
                }
            })

            answerColumns.forEach(answerColumn => {
                const exist = columns.find(column => column === answerColumn);
                if (!exist) {
                    columns.push(answerColumn);
                }
            })
        } else {
            rows.forEach(row => {
                matrixChoicesMap[row] = []
            })
        }

        return {
            id: this.question.id,
            content: this.question.content,
            status: this.question.status,
            adminQuestion: this.question.question,
            Constants,
            showInvalid: false,
            matrixChoices,
            matrixChoicesMap,
            openPhotoViewer: false,
            rows,
            columns,
        };
    },
    computed: {
        title: function() {
            return this.content.meta.title || '';
        },

        photo() {
            if (this.question.image_file_name) {
                return [{
                    path: this.question.image_file_name
                }];
            } else {
                return [];
            }
        },

        hasAnswer() {
            return this.matrixChoices.length > 0;
        },

        hasValidAnswer() {
            if (this.rowAnswerRequired) {
                const rowList = Object.values(this.matrixChoicesMap)
                return rowList.filter(row => row.length > 0).length === rowList.length
            } else {
                return this.matrixChoices.length > 0;
            }
        },

        formattedText() {
            const text = this.content.data.text;
            return text.nl2brIfNotHTML();
        },

        isMultiple() {
            return this.adminQuestion.answer_type === Constants.Question.AnswerType.MultipleMatrix
        },

        rowAnswerRequired() {
            return this.content.data.row_answer_required
        }
    },
    methods: {
        isChecked(row, column) {
            const choice = `${row}/${column}`;

            return this.matrixChoices.find(checkee => checkee === choice);
        },
        toggle(row, column) {
            const choice = `${row}/${column}`;

            if (!this.matrixChoicesMap[row]) {
                this.matrixChoicesMap[row] = [];
            }
            
            const currentIndex = this.matrixChoicesMap[row].indexOf(choice);
            if (this.isMultiple) {
                if (currentIndex >= 0) {
                    this.matrixChoicesMap[row].splice(currentIndex, 1);
                } else {
                    this.matrixChoicesMap[row].push(choice);
                }
            } else {
                if (currentIndex >= 0) {
                    this.matrixChoicesMap[row] = [];
                } else {
                    this.matrixChoicesMap[row] = [choice];
                }
            }
            this.matrixChoices = Object.values(this.matrixChoicesMap).flat();
        },

        highlightInvalidState() {
            this.showInvalid = true;
        },

        getDataToPersist() {
            return {
                question_id: this.id,
                selected_option_ids: [],
                answer: {
                    matrixChoices: this.matrixChoices,
                    matrixChoicesMap: this.matrixChoicesMap
                },
                answer_id: this.answer ? this.answer.id : null
            };
        }
    }
}
</script>

<style scoped lang="scss">
img {
    max-width: max-content;
    width: 100%;
    border-radius: 4px;
}

.required-star {
    color: red;
    font-weight: bold;
}

table {
    text-align: center;

    th, td {
        padding: 8px;
    }

    input[type=checkbox], input[type=radio] {
        width: 16px;
        height: 16px;
    }
}

</style>

<i18n>
{
    "en": {
        "click_to_select": "Click to select",
        "click_to_edit": "Click to edit",
        "row_answer_required": "Each row must have one response."
    },
    "zh": {
        "click_to_select": "點此選擇",
        "click_to_edit": "點此編輯",
        "row_answer_required": "每列須有一則回應"
    }
}
</i18n>